import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, Navigation, NavigationOff, Filter } from "lucide-react";
import FilterChips from "./FilterChips";
import UserMenu from "./UserMenu";
import { useAuth } from '../contexts/AuthContext';
import { useReducedMotion } from "../hooks/useReducedMotion";

interface MobileHeaderProps {
  filterTags: { id: string; icon: string; title: string }[];
  selectedFilters: string[];
  isDarkMode: boolean;
  showFilters: boolean;
  toggleFilter: (id: string) => void;
  showListModal: boolean;
  showMyShopsModal: boolean;
  showSettings: boolean;
  isTrackingLocation: boolean;
  toggleLocationTracking: () => void;
  setShowFilters: (show: boolean) => void;
  setShowListModal: () => void;
  setShowMyShopsModal: () => void;
  setShowSettings: (show: boolean) => void;
  setIsUserMenuOpen: (open: boolean) => void;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({
  filterTags,
  selectedFilters,
  isDarkMode,
  showFilters,
  toggleFilter,
  showListModal,
  showSettings,
  isTrackingLocation,
  toggleLocationTracking,
  setShowFilters,
  setShowListModal,
  setShowMyShopsModal,
  setShowSettings,
  setIsUserMenuOpen,
}) => {
  const { loading } = useAuth();
  const { shouldReduceMotion, animationConfig } = useReducedMotion();

  const handleFilterPanelDragEnd = (event: any, info: any) => {
    // Close filter panel if swiped down with sufficient velocity or distance
    if (info.offset.y > 50 || info.velocity.y > 500) {
      setShowFilters(false);
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[200] md:hidden pointer-events-auto" style={{ pointerEvents: 'auto' }}>
      {/* Mobile Filter Tags - Above the header */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={shouldReduceMotion ? { duration: 0 } : {
              duration: 0.2,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 100 }}
            dragElastic={0.2}
            onDragEnd={handleFilterPanelDragEnd}
            dragDirectionLock
            className="absolute bottom-full left-0 right-0 mb-2 px-4 pointer-events-auto"
            style={{ willChange: 'transform, opacity' }}
          >
            <div>
              <FilterChips
                filterTags={filterTags}
                selectedFilters={selectedFilters}
                isDarkMode={isDarkMode}
                onToggleFilter={toggleFilter}
                className="w-full"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <header
        className={`
          ${isDarkMode ? "bg-gray-900 border-t border-gray-800" : "bg-white border-t border-gray-200"}
          w-full flex items-center pb-[env(safe-area-inset-bottom)]
        `}
        style={{ height: 'calc(var(--mobile-nav-height) + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="px-6 w-full">
          <div className="flex items-center justify-between max-w-sm mx-auto">

            {/* Location Tracking */}
            <button
              onClick={toggleLocationTracking}
              className="flex flex-col items-center gap-1 min-w-[64px]"
            >
              <div className={`p-2 rounded-xl transition-all duration-300 ${isTrackingLocation
                ? `${isDarkMode ? "bg-green-500/20 text-green-400" : "bg-green-50 text-green-600"}`
                : `${isDarkMode ? "text-gray-400" : "text-gray-500"}`
                } location-tracking`}>
                {isTrackingLocation ? <Navigation className="h-6 w-6" /> : <NavigationOff className="h-6 w-6" />}
              </div>
              <span className={`text-[10px] font-medium ${isTrackingLocation ? (isDarkMode ? "text-green-400" : "text-green-600") : "text-gray-500"}`}>
                Locate
              </span>
            </button>

            {/* List View */}
            <button
              onClick={() => setShowListModal()}
              className="flex flex-col items-center gap-1 min-w-[64px]"
            >
              <div className={`p-2 rounded-xl transition-all duration-300 ${showListModal
                ? `${isDarkMode ? "bg-blue-500/20 text-blue-400" : "bg-blue-50 text-blue-600"}`
                : `${isDarkMode ? "text-gray-400" : "text-gray-500"}`
                }`}>
                <List className="h-6 w-6" />
              </div>
              <span className={`text-[10px] font-medium ${showListModal ? (isDarkMode ? "text-blue-400" : "text-blue-600") : "text-gray-500"}`}>
                List
              </span>
            </button>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex flex-col items-center gap-1 min-w-[64px]"
            >
              <div className={`p-2 rounded-xl transition-all duration-300 ${showFilters
                ? `${isDarkMode ? "bg-blue-500/20 text-blue-400" : "bg-blue-50 text-blue-600"}`
                : `${isDarkMode ? "text-gray-400" : "text-gray-500"}`
                }`}>
                <Filter className={`h-6 w-6 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </div>
              <span className={`text-[10px] font-medium ${showFilters ? (isDarkMode ? "text-blue-400" : "text-blue-600") : "text-gray-500"}`}>
                Filter
              </span>
            </button>

            {/* User Menu */}
            <div className="flex flex-col items-center gap-1 min-w-[64px]">
              {loading ? (
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
              ) : (
                <UserMenu
                  isDarkMode={isDarkMode}
                  onOpenMyShops={setShowMyShopsModal}
                  onOpenSettings={() => setShowSettings(!showSettings)}
                  onMenuStateChange={(isOpen) => setIsUserMenuOpen(isOpen)}
                />
              )}
              <span className="text-[10px] font-medium text-gray-500">
                Menu
              </span>
            </div>

          </div>
        </div>
      </header>
    </div>
  );
};

export default MobileHeader;
